import { html, nothing, unsafeCSS, type CSSResultGroup, type PropertyValues } from 'lit';
import { live } from 'lit/directives/live.js';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import { formatValue, parseValue } from '../time-range-picker/lib-time-range-picker';
import '../button/lib-button';
import '../calendar/lib-calendar';
import '../flyout/lib-flyout';
import '../icon/lib-icon';
import '../textbox/lib-textbox';
import '../time-picker/lib-time-picker';
import css from './lib-date-time-picker.css';

interface ValueElement extends HTMLElement { value: string; }
const icons = { calendar: new URL('../wago-icons-svg/calendar.svg', import.meta.url).href };
const pad = (value: number, length = 2): string => String(value).padStart(length, '0');
const today = (): string => { const date = new Date(); return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`; };

@customElement('lib-date-time-picker')
export class LibDateTimePicker extends LibElement {
  protected override get hostFocusSelector(): string { return '.lib-date-time-picker-summary, .lib-date-time-picker-field > lib-textbox'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];
  private revision = 0;
  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'server-state']; }
  override attributeChangedCallback(name: string, previous: string | null, value: string | null): void {
    if (name === 'server-state') {
      if (value !== null) {
        const state = JSON.parse(value) as Record<string, string> & { revision: number };
        if (state.revision >= this.revision) {
          this.value = state.value;
        }
      }
      return;
    }
    if (['value'].includes(name) && this.hasAttribute('server-state')) return;
    super.attributeChangedCallback(name, previous, value);
  }
  protected override createRenderRoot(): HTMLElement | DocumentFragment {
    const root = super.createRenderRoot();
    // Capture before nested controls stop propagation; rendering here could replace native input before its handler reads it.
    root.addEventListener('input', () => { this.revision++; }, true);
    // A partial option selection changes the nested time draft without a native change.
    const childRevisions = new WeakMap<EventTarget, number>();
    root.addEventListener('lib-time-picker-input', event => {
      const revision = (event as CustomEvent<{ revision: number }>).detail.revision;
      if (event.target && revision > (childRevisions.get(event.target) ?? 0)) {
        childRevisions.set(event.target, revision);
        this.revision++;
        this.requestUpdate();
      }
    }, true);
    return root;
  }
  @property() value = '';
  @property({ attribute: 'today-date' }) todayDate = '';
  @property() min = '';
  @property() max = '';
  @property() format = 'yyyy-MM-dd HH:mm';
  @property() surface = 'white';
  @property({ attribute: false }) months: string[] = [];
  @property({ attribute: false }) weekdays: string[] = [];
  @property({ attribute: 'date-time-label' }) dateTimeLabel = '';
  @property({ attribute: 'open-label' }) openLabel = '';
  @property({ attribute: 'apply-label' }) applyLabel = '';
  @property({ attribute: 'invalid-format-message' }) invalidFormatMessage = '';
  @property({ attribute: 'out-of-range-message' }) outOfRangeMessage = '';
  @property({ attribute: 'before-min-message' }) beforeMinMessage = '';
  @property({ attribute: 'after-max-message' }) afterMaxMessage = '';
  @property({ attribute: 'calendar-label' }) calendarLabel = '';
  @property({ attribute: 'today-label' }) todayLabel = '';
  @property({ attribute: 'previous-month-label' }) previousMonthLabel = '';
  @property({ attribute: 'next-month-label' }) nextMonthLabel = '';
  @property({ attribute: 'previous-year-label' }) previousYearLabel = '';
  @property({ attribute: 'next-year-label' }) nextYearLabel = '';
  @property({ attribute: 'time-label' }) timeLabel = '';
  @property({ attribute: 'hours-label' }) hoursLabel = '';
  @property({ attribute: 'minutes-label' }) minutesLabel = '';
  @property({ attribute: 'invalid-hour-message' }) invalidHourMessage = '';
  @property({ attribute: 'invalid-minute-message' }) invalidMinuteMessage = '';
  @property({ attribute: 'select-hour-message' }) selectHourMessage = '';
  @property({ attribute: 'select-minute-message' }) selectMinuteMessage = '';
  @property({ attribute: 'unfinished-message' }) unfinishedMessage = '';
  @property({ attribute: 'time-out-of-range-message' }) timeOutOfRangeMessage = '';
  @property({ attribute: 'time-before-min-message' }) timeBeforeMinMessage = '';
  @property({ attribute: 'time-after-max-message' }) timeAfterMaxMessage = '';
  @property() culture = 'en-US';
  @property({ type: Number, attribute: 'first-day' }) firstDay = 0;
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) error = false;
  @property() message = '';
  @property({ attribute: 'aria-label' }) override ariaLabel: string | null = null;
  @state() private text = '';
  @state() private fieldError = '';
  @state() private open = false;
  @state() private draft: string | null = null;
  @state() private pickerError = '';
  private reportedRevision = -1;
  private reported = '';

  private t(key: keyof typeof textProperties, fallback: string): string { return this[textProperties[key]] || fallback; }
  private display(value: string): string { return formatValue(value, this.format); }
  private selected(): string { return (this.draft ?? this.value) || `${this.todayDate || today()}T00:00`; }
  private fill(template: string, ...values: string[]): string { return values.reduce((text, value, index) => text.replace(`{${index}}`, value), template); }

  protected override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has('value') || changed.has('min') || changed.has('max')) this.resetDraft();
    if (changed.has('value') || changed.has('format')) this.text = this.display(this.value);
    if (changed.has('disabled') && this.disabled) this.dismiss();
  }
  protected override updated(): void {
    if (this.fieldError === this.reported && this.reportedRevision === this.revision) return;
    this.reportedRevision = this.revision;
    this.reported = this.fieldError;
    this.emit('lib-date-time-picker-input', { error: this.fieldError, revision: this.revision });
  }

  private validate(value: string): string {
    if (this.min && this.max && (value < this.min || value > this.max)) return this.fill(this.t('outOfRange', 'Choose a date and time between {0} and {1}.'), this.display(this.min), this.display(this.max));
    if (this.min && value < this.min) return this.fill(this.t('beforeMin', 'Choose {0} or later.'), this.display(this.min));
    if (this.max && value > this.max) return this.fill(this.t('afterMax', 'Choose {0} or earlier.'), this.display(this.max));
    return '';
  }
  private clamp(value: string): string {
    if (this.min && value < this.min) return this.min;
    if (this.max && value > this.max) return this.max;
    return value;
  }
  private commit(value: string, draft = false): void {
    this.fieldError = this.validate(value);
    if (this.fieldError) return;
    this.text = this.display(value);
    if (draft) { this.draft = value; return; }
    this.resetDraft();
    if (value === this.value) return;
    this.value = value;
    this.emit('lib-date-time-picker-change', { value, revision: this.revision });
  }
  private textChange(event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;
    const raw = (event.target as ValueElement).value;
    this.text = raw;
    const value = parseValue(raw, this.format);
    if (value === null) { this.fieldError = this.fill(this.t('invalidFormat', 'Enter the date and time as {0}.'), this.format); return; }
    this.commit(value);
  }
  private dateChange(event: Event): void {
    this.revision++;
    event.stopPropagation();
    const date = (event.target as ValueElement).value;
    if (this.disabled || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    this.commit(this.clamp(`${date}T${this.selected().slice(11) || '00:00'}`), true);
  }
  private timeChange(event: Event): void {
    this.revision++;
    event.stopPropagation();
    const time = (event.target as ValueElement).value;
    if (this.disabled || !/^\d{2}:\d{2}$/.test(time)) return;
    this.commit(this.clamp(`${this.selected().slice(0, 10)}T${time}`), true);
  }
  private toggle(): void { if (!this.disabled) { const wasOpen = this.open; this.dismiss(); if (!wasOpen) { this.open = true; this.resetDraft(); } } }
  private resetDraft(): void { this.draft = this.open ? this.value || this.clamp(`${this.todayDate || today()}T00:00`) : null; this.text = this.display(this.value); this.fieldError = ''; this.pickerError = ''; }
  private dismiss(invalidate = true): void {
    if (invalidate) {
      this.revision++;
    }
    this.open = false;
    this.resetDraft();
  }
  private apply(): void {
    if (this.disabled || this.pickerError || this.fieldError || !this.draft) return;
    this.commit(this.draft);
    if (!this.fieldError) this.dismiss(false);
  }
  private stop(event: Event): void { event.stopPropagation(); }

  private pickers() {
    const value = this.selected(), date = value.slice(0, 10), lower = this.min, upper = this.max;
    const minMinute = date.length > 0 && lower.slice(0, 10) === date ? Number(lower.slice(11, 13)) * 60 + Number(lower.slice(14, 16)) : null;
    const maxMinute = date.length > 0 && upper.slice(0, 10) === date ? Number(upper.slice(11, 13)) * 60 + Number(upper.slice(14, 16)) : null;
    return html`<div class="lib-date-time-picker-pickers">
      <lib-calendar today-date=${this.todayDate} .selectedDate=${live(date)} .minDate=${lower.slice(0, 10)} .maxDate=${upper.slice(0, 10)} .months=${this.months} .weekdays=${this.weekdays}
        culture=${this.culture} first-day=${this.firstDay} ?disabled=${this.disabled} grid-label=${this.t('calendar', 'Calendar')} today-label=${this.t('today', 'Today')}
        previous-month-label=${this.t('previousMonth', 'Previous month')} next-month-label=${this.t('nextMonth', 'Next month')}
        previous-year-label=${this.t('previousYear', 'Previous year')} next-year-label=${this.t('nextYear', 'Next year')}
        @change=${this.dateChange} @input=${this.stop}></lib-calendar>
      <div class="lib-date-time-picker-divider" aria-hidden="true"></div>
      <lib-time-picker .value=${live(value.slice(11))} .minMinute=${minMinute} .maxMinute=${maxMinute} ?disabled=${this.disabled}
        group-label=${this.t('time', 'Time')} hours-label=${this.t('hours', 'Hours')} minutes-label=${this.t('minutes', 'Minutes')}
        invalid-hour-message=${this.t('invalidHour', 'Enter an hour from 00 to 23.')} invalid-minute-message=${this.t('invalidMinute', 'Enter minutes from 00 to 59.')}
        select-hour-message=${this.t('selectHour', 'Select an hour.')} select-minute-message=${this.t('selectMinute', 'Select minutes.')}
        unfinished-message=${this.t('unfinished', 'Finish entering the time.')} range-message=${this.t('timeOutOfRange', 'Choose a time within the allowed range.')}
        @change=${this.timeChange} @lib-time-picker-input=${(event: CustomEvent<{ error: string }>) => { event.stopPropagation(); this.pickerError = event.detail.error; }}></lib-time-picker>
    </div>`;
  }

  override render() {
    const invalid = this.error || !!this.fieldError, showMessage = !!this.message && !this.fieldError;
    return html`<div class="lib-date-time-picker lib-date-time-picker-surface-${this.surface} ${this.disabled ? 'lib-date-time-picker--disabled' : ''}"
      role="group" aria-label=${this.ariaLabel || this.t('dateTime', 'Date and time')} aria-describedby=${showMessage ? 'message' : nothing}>
      <div class="lib-date-time-picker-anchor"><lib-textbox aria-label=${this.ariaLabel || this.t('dateTime', 'Date and time')} surface=${this.surface} .value=${live(this.text)} bind-mode="onlostfocus" placeholder=${this.format} maxlength="40"
          ?disabled=${this.disabled} ?error=${invalid} message=${this.fieldError} data-lib-flyout-anchor="picker-popup" @change=${this.textChange} @input=${this.stop}>
          <button slot="icon" type="button" class="lib-date-time-picker-trigger" aria-label=${this.t('open', 'Choose date and time')} title=${this.t('open', 'Choose date and time')}
            ?disabled=${this.disabled} aria-haspopup="dialog" aria-expanded=${String(this.open)} aria-controls="picker-popup" @click=${this.toggle}><lib-icon icon-url=${icons.calendar}></lib-icon></button>
        </lib-textbox>
        <lib-flyout id="picker-popup" alignment="start" custom-surface popover="manual" ?open=${this.open} @lib-flyout-dismiss=${() => { if (this.open) this.dismiss(); }}>
          <div class="lib-date-time-picker-surface lib-date-time-picker-panel" role="dialog" aria-label=${this.t('dateTime', 'Date and time')}>
            ${this.pickers()}<div class="lib-date-time-picker-actions"><lib-button variant="primary" ?disabled=${this.disabled || !!this.pickerError || !!this.fieldError || !this.draft} @click=${this.apply}>${this.t('apply', 'Apply')}</lib-button></div>
          </div>
        </lib-flyout>
      </div>${showMessage ? html`<div id="message" class="lib-date-time-picker-message">${this.message}</div>` : nothing}
    </div>`;
  }
}
declare global { interface HTMLElementTagNameMap { 'lib-date-time-picker': LibDateTimePicker; } }

const textProperties = {
  dateTime: 'dateTimeLabel', open: 'openLabel', apply: 'applyLabel', invalidFormat: 'invalidFormatMessage', outOfRange: 'outOfRangeMessage', beforeMin: 'beforeMinMessage', afterMax: 'afterMaxMessage',
  calendar: 'calendarLabel', today: 'todayLabel', previousMonth: 'previousMonthLabel', nextMonth: 'nextMonthLabel', previousYear: 'previousYearLabel', nextYear: 'nextYearLabel', time: 'timeLabel', hours: 'hoursLabel', minutes: 'minutesLabel',
  invalidHour: 'invalidHourMessage', invalidMinute: 'invalidMinuteMessage', selectHour: 'selectHourMessage', selectMinute: 'selectMinuteMessage', unfinished: 'unfinishedMessage', timeOutOfRange: 'timeOutOfRangeMessage', timeBeforeMin: 'timeBeforeMinMessage', timeAfterMax: 'timeAfterMaxMessage',
} as const;
export function setDateTimePickerData(element: LibDateTimePicker | null, months: string[], weekdays: string[]): void {
  if (!element) return;
  element.months = months;
  element.weekdays = weekdays;
}
